import type { WidgetEvent } from '@universal-input/contracts';

export interface MountOptions {
  container: HTMLElement | string;
  widgetOrigin: string;
  profileKey: string;
  sessionId: string;
  launchToken: string;
  height?: number;
  onEvent?: (event: WidgetEvent) => void;
}

export interface WidgetHandle {
  iframe: HTMLIFrameElement;
  destroy(): void;
}

export function mount(options: MountOptions): WidgetHandle {
  const container = typeof options.container === 'string'
    ? document.querySelector<HTMLElement>(options.container)
    : options.container;
  if (!container) throw new Error('Universal Input container was not found');

  const widgetOrigin = new URL(options.widgetOrigin).origin;
  const iframe = document.createElement('iframe');
  iframe.src = `${widgetOrigin}/embed/${encodeURIComponent(options.profileKey)}`;
  iframe.title = 'Universal Input';
  iframe.allow = 'camera; microphone';
  iframe.referrerPolicy = 'strict-origin';
  iframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-same-origin allow-downloads');
  iframe.style.width = '100%';
  iframe.style.height = `${options.height ?? 540}px`;
  iframe.style.border = '0';
  iframe.style.display = 'block';
  iframe.style.background = '#fff';
  container.replaceChildren(iframe);

  let initialized = false;
  const receive = (message: MessageEvent<unknown>) => {
    if (message.origin !== widgetOrigin || message.source !== iframe.contentWindow || !isEnvelope(message.data)) return;
    const event = message.data.event;
    if (event.type === 'ready' && !initialized) {
      initialized = true;
      iframe.contentWindow?.postMessage({
        type: 'universal-input:init',
        sessionId: options.sessionId,
        profileKey: options.profileKey,
        launchToken: options.launchToken,
      }, widgetOrigin);
    }
    options.onEvent?.(event);
  };
  window.addEventListener('message', receive);

  return {
    iframe,
    destroy() {
      window.removeEventListener('message', receive);
      iframe.remove();
    },
  };
}

function isEnvelope(value: unknown): value is { channel: 'universal-input'; event: WidgetEvent } {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  if (record.channel !== 'universal-input' || !record.event || typeof record.event !== 'object') return false;
  return typeof (record.event as Record<string, unknown>).type === 'string';
}

export const UniversalInput = { mount };

